import { BodyScrollEvent, ColDef, GridApi, GridReadyEvent, ICellRendererParams, RowClickedEvent, SortChangedEvent } from "ag-grid-community"
import { AgGridReact } from "ag-grid-react"
import { GridTheme } from "modules/common/components/table/GridTheme"
import { DefaultAgGridOptions, rowIdBy } from "modules/common/services/table/helpers"
import { agGridLocale } from "modules/root/services/locale"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import styles from "./QuestionsList.module.scss"

/** Строка таблицы заданий: простые значения, уже разрешённые по доскам и колонкам */
export interface QuestionGridRow {
  id: string
  number: string
  title: string
  parentId: string
  parentNumber: string
  parentTitle: string
  orderName: string
  boardName: string
  columnName: string
  columnColor: string
  stopDay: string
  expired: boolean
  created: string
  people: string
}

/** Сколько строк до конца, когда пора догружать следующую страницу */
const LOAD_AHEAD = 5

const getRowId = rowIdBy((row: QuestionGridRow) => row.id)

const options = DefaultAgGridOptions(true)

/** Задание и, у подзадания, — к какому заданию оно относится */
const TitleCell = ({ data }: ICellRendererParams<QuestionGridRow>) =>
  data ? (
    <div className={styles.titleCell}>
      <span className={styles.title}>{data.title || `Задание ${data.number}`}</span>
      {data.parentId && (
        <span className={styles.parent} title={data.parentTitle}>
          ↳ подзадание к {data.parentNumber} {data.parentTitle}
        </span>
      )}
    </div>
  ) : null

/** Колонка доски, где лежит задание: её название и цвет, как в шапке колонки на доске */
const ColumnCell = ({ data }: ICellRendererParams<QuestionGridRow>) =>
  data && data.columnName ? (
    <span className={styles.status} style={{ backgroundColor: data.columnColor }} title={data.columnName}>
      {data.columnName}
    </span>
  ) : null

/** Сортировку делает сервер: грид порядок строк не меняет, только показывает стрелку */
const keepOrder = () => 0

/**
 * Таблица списка заданий на AG Grid, как остальные таблицы приложения. Строки догружаются
 * при прокрутке, сортировка по сроку и дате создания — на сервере
 */
export const QuestionsGrid: React.FC<QuestionsGridProps> = ({
  rows,
  selectedId,
  wide,
  sort,
  desc,
  loaded,
  onSelect,
  onSort,
  onNearEnd,
}) => {
  const api = useRef<GridApi | null>(null)

  const columnDefs = useMemo<ColDef<QuestionGridRow>[]>(
    () => [
      { headerName: "№", field: "number", width: 90, sortable: false },
      { headerName: "Задание", field: "title", flex: 1, minWidth: 220, sortable: false, cellRenderer: TitleCell },
      { headerName: "Объект", field: "orderName", width: 200, sortable: false, hide: !wide, tooltipField: "orderName" },
      { headerName: "Доска", field: "boardName", width: 150, sortable: false, hide: !wide, tooltipField: "boardName" },
      { headerName: "Колонка", field: "columnName", width: 140, sortable: false, cellRenderer: ColumnCell },
      {
        headerName: "Срок",
        colId: "deadline",
        field: "stopDay",
        width: 110,
        sortable: true,
        comparator: keepOrder,
        valueFormatter: ({ value }) => value || "—",
        cellClassRules: { [styles.late]: ({ data }) => !!data && data.expired },
      },
      {
        headerName: "Создано",
        colId: "created",
        field: "created",
        width: 110,
        sortable: true,
        comparator: keepOrder,
        hide: !wide,
      },
      { headerName: "Участники", field: "people", width: 220, sortable: false, hide: !wide, tooltipField: "people" },
    ],
    [wide]
  )

  // стрелка сортировки — по тому, что выбрано в списке
  const applySort = useCallback(() => {
    api.current &&
      api.current.applyColumnState({
        state: [{ colId: sort, sort: desc ? "desc" : "asc" }],
        defaultState: { sort: null },
      })
  }, [sort, desc])

  useEffect(applySort, [applySort])

  // выбранное задание из адреса подсвечено в таблице
  const markSelected = useCallback(() => {
    api.current && api.current.forEachNode((node) => node.setSelected(!!node.data && node.data.id === selectedId))
  }, [selectedId])

  useEffect(markSelected, [markSelected, rows])

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      api.current = e.api
      applySort()
      markSelected()
    },
    [applySort, markSelected]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      // только щелчок по шапке: свой applyColumnState сюда тоже попадает
      if (e.source !== "uiColumnSorted") {
        return
      }

      const sorted = e.api.getColumnState().find((c) => c.sort)
      sorted && onSort(sorted.colId, sorted.sort === "desc")
    },
    [onSort]
  )

  const onRowClicked = useCallback((e: RowClickedEvent<QuestionGridRow>) => e.data && onSelect(e.data.id), [onSelect])

  // догрузка: до конца списка осталось несколько строк
  const onBodyScroll = useCallback(
    (e: BodyScrollEvent) => {
      if (e.direction === "vertical" && e.api.getLastDisplayedRowIndex() >= rows.length - LOAD_AHEAD) {
        onNearEnd()
      }
    },
    [rows.length, onNearEnd]
  )

  return (
    <GridTheme bordered={true} className={`designed ${styles.grid}`}>
      <AgGridReact<QuestionGridRow>
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={getRowId}
        gridOptions={options}
        localeText={agGridLocale}
        rowHeight={44}
        headerHeight={36}
        // выделение ставит сам список по адресу задания; клик выделение не меняет
        rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: false }}
        suppressCellFocus={true}
        enableCellTextSelection={true}
        overlayNoRowsTemplate={loaded ? "Заданий не найдено. Попробуйте изменить фильтры" : " "}
        onGridReady={onGridReady}
        onSortChanged={onSortChanged}
        onRowClicked={onRowClicked}
        onBodyScroll={onBodyScroll}
        onRowDataUpdated={markSelected}
      />
    </GridTheme>
  )
}

interface QuestionsGridProps {
  rows: QuestionGridRow[]
  selectedId: string
  /** Карточка закрыта — показываем все колонки; открыта — только номер, задание, колонку и срок */
  wide: boolean
  sort: string
  desc: boolean
  /** Список уже загружали: до этого «ничего не найдено» не показываем */
  loaded: boolean
  onSelect: (id: string) => void
  onSort: (sort: string, desc: boolean) => void
  onNearEnd: () => void
}
